import axios from 'axios';
import { clearAuthSession, getAuthToken } from './authStorage';
import { showTwilioAdminPopup } from './notificationBus';

// Default to same-origin requests so changing frontend port does not require code changes.
export const API_ROOT = (import.meta.env.VITE_API_ROOT || '').replace(/\/$/, '');

export const resolveApiUrl = (path = '') => {
    if (!path) return API_ROOT;
    if (/^https?:\/\//i.test(path)) return path;
    const normalizedPath = String(path).startsWith('/') ? path : `/${path}`;
    return `${API_ROOT}${normalizedPath}`;
};

/**
 * Extract a human-readable error message from any API error response.
 *
 * Handles both the standardised envelope  { error: { code, message, timestamp } }
 * and legacy string formats               { error: "...", message: "..." }
 * so old and new backend responses work without separate handling in every component.
 */
export const extractApiError = (error) => {
    const data = error?.response?.data;
    if (!data) return error?.message || 'An unexpected error occurred';
    // Standardised format: { error: { code, message, timestamp } }
    if (data?.error?.message) return String(data.error.message);
    // Legacy string format: { error: "..." }
    if (typeof data?.error === 'string') return data.error;
    // Plain message field
    if (data?.message) return String(data.message);
    return error?.message || 'An unexpected error occurred';
};

let pendingRequests = 0;
const updateLoader = (delta) => {
    pendingRequests += delta;
    if (pendingRequests < 0) pendingRequests = 0;
    window.dispatchEvent(new CustomEvent('loader:toggle', { detail: { loading: pendingRequests > 0 } }));
};

axios.defaults.baseURL = API_ROOT;

// ─── Request interceptor ──────────────────────────────────────────────────────
axios.interceptors.request.use((config) => {
    updateLoader(1);

    const token = getAuthToken();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }

    // Forward the server-issued X-Request-ID on subsequent requests so log
    // correlation works across page interactions.
    const lastRequestId = sessionStorage.getItem('last_request_id');
    if (lastRequestId) {
        config.headers['X-Request-ID'] = lastRequestId;
    }

    return config;
}, (error) => {
    updateLoader(-1);
    return Promise.reject(error);
});

// ─── Response interceptor ─────────────────────────────────────────────────────
axios.interceptors.response.use(
    (response) => {
        updateLoader(-1);
        // Store the request ID echoed by the server for correlation on next call.
        const reqId = response.headers?.['x-request-id'];
        if (reqId) sessionStorage.setItem('last_request_id', reqId);
        return response;
    },
    (error) => {
        updateLoader(-1);

        if (error?.response?.status === 401) {
            clearAuthSession();
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }

        const responseData = error?.response?.data;
        const errorBlob = JSON.stringify(responseData || {}).toLowerCase();
        const directMessage = extractApiError(error);

        const isTwilioOrSttError =
            /twilio|stt|voice|whatsapp/.test(errorBlob) ||
            /twilio|stt|voice/.test(String(error?.message || '').toLowerCase());

        if (isTwilioOrSttError) {
            const endpoint = String(error?.config?.url || '').split('?')[0] || 'unknown-endpoint';
            const status = error?.response?.status || 'no-status';
            showTwilioAdminPopup(String(directMessage), {
                dedupeKey: `api:${endpoint}:${status}:${String(directMessage)}`,
            });
        }

        return Promise.reject(error);
    },
);
